import React, { useState, useEffect } from "react";
import { Row, Col, FormGroup, Label, Input, Button } from "reactstrap";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { useForm, useController } from "react-hook-form";
import {
  getDriverDeductionByAll,
  createDeductionType,
  updateDriverTypeDeduction as updateSchemeDeduction
} from "../../../Services/Driver.service";
import { showAlert } from "../../../Services/Shared.service";
import { Spinner } from "../../../AbstractElements";
import SaveIcon from "@mui/icons-material/Save";

import {
  VALUE_TYPE_OPTIONS,
  CALC_TYPE_OPTIONS,
  BOOKING_TYPE_OPTIONS
} from "./DeductionConstants";

const SingleDeductionForm = ({ deduction, onClose, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [existingTypes, setExistingTypes] = useState([]);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const { control, handleSubmit, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      deductionType: { label: deduction.driverDeductionName, value: deduction.driverDeductionId },
      valueType: VALUE_TYPE_OPTIONS.find(o => o.value === deduction.valueType) || VALUE_TYPE_OPTIONS[0],
      value: deduction.value || "",
      calculationType: CALC_TYPE_OPTIONS.find(o => o.value === deduction.calculateType) || CALC_TYPE_OPTIONS[0],
      bookingType: BOOKING_TYPE_OPTIONS.find(o => o.value === deduction.bookingTypeDeduction) || BOOKING_TYPE_OPTIONS.find(o => o.value === "All"),
    }
  });

  const deductionTypeField = useController({
    name: "deductionType",
    control,
    rules: { required: "Deduction Type is required" }
  }).field;

  const valueTypeField = useController({
    name: "valueType",
    control,
    rules: { required: "Value Type is required" }
  }).field;

  const valueField = useController({
    name: "value",
    control,
    rules: {
      required: "Value is required",
      validate: (val) => {
        if (parseFloat(val) < 0) {
          return "Value cannot be less than 0";
        }
        const valType = watch("valueType")?.value;
        if (valType === "P" && parseFloat(val) > 100) {
          return "Percentage value cannot be greater than 100";
        }
        return true;
      }
    }
  }).field;

  const calculationTypeField = useController({
    name: "calculationType",
    control,
    rules: { required: "Calculation Type is required" }
  }).field;

  const bookingTypeField = useController({
    name: "bookingType",
    control,
    rules: { required: "Booking Type is required" }
  }).field;

  useEffect(() => {
    fetchTypes();
  }, []);

  const fetchTypes = async () => {
    try {
      const resp = await getDriverDeductionByAll();
      const dataArr = resp.data?.searchResponse || resp.searchResponse || [];
      const types = dataArr.map((t) => ({
        label: t.name || t.deductionType,
        value: t.id,
        calculateOn: t.calculateOn,
      }));
      setExistingTypes(types);
    } catch (e) {
      console.error("Error fetching types", e);
    }
  };

  const watchedDeductionType = watch("deductionType");

  useEffect(() => {
    if (watchedDeductionType && !watchedDeductionType.__isNew__ && watchedDeductionType.calculateOn) {
      const matchedCalc = CALC_TYPE_OPTIONS.find(o => o.value === watchedDeductionType.calculateOn);
      if (matchedCalc) {
        setValue("calculationType", matchedCalc);
      }
    } else if (!watchedDeductionType) {
      setValue("calculationType", CALC_TYPE_OPTIONS[0]);
    }
  }, [watchedDeductionType, setValue]);

  const onSubmit = async (formValues) => {
    setLoading(true);
    try {
      let typeId = formValues.deductionType.value;

      if (formValues.deductionType.__isNew__) {
        const typeResp = await createDeductionType({
          deductionType: formValues.deductionType.label,
          deductionDisplayName: formValues.deductionType.label,
          calculateOn: formValues.calculationType.value
        });
        typeId = typeResp.data.id;
      }

      const updateObj = {
        id: deduction.id,
        driverSchemeId: deduction.driverSchemeId,
        serviceCityId: deduction.serviceCityId,
        driverDeductionId: typeId,
        valueType: formValues.valueType.value,
        value: parseFloat(formValues.value),
        calculateType: formValues.calculationType.value,
        bookingTypeDeduction: formValues.bookingType.value,
      };

      await updateSchemeDeduction(updateObj);
      showAlert("success", "Deduction rule updated successfully!");
      onRefresh();
      onClose();
    } catch (error) {
      console.error("Error updating deduction:", error);
      const msg =
        error.response?.data?.message ||
        (typeof error.response?.data === "string" ? error.response.data : null) ||
        error.message ||
        "Failed to update deduction.";
      showAlert("error", msg);
    } finally {
      setLoading(false);
    }
  };

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      borderRadius: "8px",
      fontSize: "14px",
      minHeight: "45px",
      backgroundColor: state.isDisabled ? "#e9ecef" : "white",
      color: state.isDisabled ? "#6c757d" : "#333",
      borderColor: state.isFocused ? "#39B54A" : "#dee2e6",
      boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(57, 181, 74, 0.25)" : "none",
      cursor: state.isDisabled ? "not-allowed" : "default",
      "&:hover": {
        borderColor: state.isFocused ? "#39B54A" : (state.isDisabled ? "#dee2e6" : "#ced4da")
      }
    }),
    singleValue: (base, state) => ({ ...base, color: state.isDisabled ? "#6c757d" : "#333" }),
    placeholder: (base) => ({ ...base, color: "#999" }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#39B54A"
        : state.isFocused
          ? "#E3F5E1"
          : "white",
      color: state.isSelected ? "white" : "#333",
      fontSize: "14px",
      cursor: "pointer",
      "&:active": {
        backgroundColor: "#39B54A",
        color: "white"
      }
    })
  };

  return (
    <div className="p-3">
      <Row>
        <Col md="12">
          <FormGroup>
            <Label className="fw-bold">Deduction Type</Label>
            <CreatableSelect
              isClearable
              options={existingTypes}
              value={deductionTypeField.value}
              onChange={deductionTypeField.onChange}
              onBlur={deductionTypeField.onBlur}
              isValidNewOption={(inputValue) => inputValue.trim().length > 0}
              placeholder="Select or Create Type..."
              styles={selectStyles}
              menuPortalTarget={document.body}
              isDisabled={!!deduction}
            />
            {errors.deductionType && (
              <span className="text-danger small mt-1 d-block">{errors.deductionType.message}</span>
            )}
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md="6">
          <FormGroup>
            <Label className="fw-bold">Value Type</Label>
            <Select
              options={VALUE_TYPE_OPTIONS}
              value={valueTypeField.value}
              onChange={valueTypeField.onChange}
              onBlur={valueTypeField.onBlur}
              styles={selectStyles}
              menuPortalTarget={document.body}
            />
            {errors.valueType && (
              <span className="text-danger small mt-1 d-block">{errors.valueType.message}</span>
            )}
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup>
            <Label className="fw-bold">Value</Label>
            <Input
              type="number"
              placeholder="0.00"
              value={valueField.value}
              onChange={valueField.onChange}
              onBlur={() => {
                valueField.onBlur();
                setIsInputFocused(false);
              }}
              onFocus={() => setIsInputFocused(true)}
              className="form-control"
              style={{
                borderRadius: "8px",
                fontSize: "14px",
                height: "45px",
                backgroundColor: "white",
                color: "#333",
                borderColor: errors.value ? "#dc3545" : (isInputFocused ? "#39B54A" : "#dee2e6"),
                boxShadow: errors.value ? "0 0 0 0.2rem rgba(220, 53, 69, 0.25)" : (isInputFocused ? "0 0 0 0.2rem rgba(57, 181, 74, 0.25)" : "none"),
                outline: "none"
              }}
            />
            {errors.value && (
              <span className="text-danger small mt-1 d-block">{errors.value.message}</span>
            )}
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md="6">
          <FormGroup>
            <Label className="fw-bold">Calculation Type</Label>
            <Select
              options={CALC_TYPE_OPTIONS}
              value={calculationTypeField.value}
              onChange={calculationTypeField.onChange}
              onBlur={calculationTypeField.onBlur}
              styles={selectStyles}
              menuPortalTarget={document.body}
              isDisabled={watchedDeductionType && !watchedDeductionType.__isNew__}
            />
            {errors.calculationType && (
              <span className="text-danger small mt-1 d-block">{errors.calculationType.message}</span>
            )}
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup>
            <Label className="fw-bold">Booking Type</Label>
            <Select
              options={BOOKING_TYPE_OPTIONS}
              value={bookingTypeField.value}
              onChange={bookingTypeField.onChange}
              onBlur={bookingTypeField.onBlur}
              styles={selectStyles}
              menuPortalTarget={document.body}
            />
            {errors.bookingType && (
              <span className="text-danger small mt-1 d-block">{errors.bookingType.message}</span>
            )}
          </FormGroup>
        </Col>
      </Row>

      <div className="d-flex justify-content-end mt-4 pt-2">
        <Button
          color="light"
          className="me-3"
          style={{ borderRadius: "10px", fontWeight: "600", padding: "10px 25px" }}
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          style={{
            borderRadius: "10px",
            fontWeight: "600",
            padding: "10px 35px",
            backgroundColor: "#39B54A",
            border: "none",
            display: "flex",
            alignItems: "center"
          }}
          onClick={handleSubmit(onSubmit)}
          disabled={loading}
        >
          {loading ? (
            <Spinner size="sm" className="me-2" />
          ) : (
            <SaveIcon fontSize="small" className="me-2" />
          )}
          Update Deduction
        </Button>
      </div>
    </div>
  );
};

export default SingleDeductionForm;
